import { RouteComponentProps } from "react-router";
import { useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  ListItem,
  ListItemText,
  useMediaQuery
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "swyft-react-core";

import { useStyles } from "./quoteDetail.styles";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { RootState } from "../../app/rootReducer";
import { fetchFinanceIrelandEstimate, fetchLead, fetchLeadReport } from "../../store/QuoteSlice";
import { TopBox } from "../../components/TopBox";
import { BotBox } from "../../components/BotBox";
import { CustomerSay } from "./components/CustomerSay";
import {
  MAX_FINAL_COST,
  MIN_FINAL_COST,
  customerSliderSolarHome,
  customerSliderSolarBusiness
} from "./quoteDetail.constants";
import { EnvironmentalImpactSection } from "./components/EnvironmentalImpactSection";
import { Title } from "./components/Title";
import { WhyChooseUsSection } from "./components/WhyChooseUsSection";
import { GraphsSection } from "./components/GraphsSection";
import { TF } from "../../components/typography/TF";
import { useQuoteInfoStyles } from "./components/QuoteInfoSection/quoteInfo.styles";
import { QuoteInfoTitle } from "./components/QuoteInfoSection/QuoteInfoTitle";
import { FinancialSupports } from "./components/FinancialSupports";
import { Product } from "../../api/Product/Product";
import { useProducts } from "../../api/Product/ProductApi";
import { EQuotePurchaserType, EQuoteType } from "../../api/Types";
import { PackageInfoSlider } from "./PackageInfoSlider";
import { solarGrantAmountForLeadProduct } from "../../app/utils";
import { Benefit } from "../checkoutPage/components";
import { BookNowButton } from "./components/BookNowButton";
import { PriceFormat } from "../../components/typography/PriceFormat";
import { RecommendedProductSlider } from "./components/RecommendedProductSlider";

type Props = RouteComponentProps<{ id: string }>;
export const OutcomePage = ({ match }: Props) => {
  const classes = useStyles();
  const { isMoreThanSm, isMoreThanMd } = useBreakpoint();
  const dispatch = useDispatch();
  const { solarReport, lead, selectedLeadProductId } = useSelector((state: RootState) => state.quote);

  const completedQuestionnaireId = match.params.id;

  useEffect(() => {
    dispatch(fetchLead({ completedQuestionnaireId: completedQuestionnaireId }));
  }, [completedQuestionnaireId, dispatch]);

  useEffect(() => {
    if (lead?.id) {
      dispatch(fetchLeadReport({ leadId: lead?.id, productId: selectedLeadProductId }));
    }
  }, [lead, selectedLeadProductId, dispatch]);

  const linkedProducts = lead?.recommendedProducts || [];
  const { data: products = [] as Product[], isLoading: productsLoading } = useProducts(
    linkedProducts.map((x) => x.id) || []
  );

  const recommendedProduct = products.find((x) => x.id === selectedLeadProductId);
  const relatedProducts = products.filter((x) => x.id !== selectedLeadProductId);

  const leadProduct = lead?.recommendedProducts.find((x) => x.id === selectedLeadProductId);

  const price = leadProduct?.oncePrice ?? 0;
  const grant = leadProduct?.grantAmount ?? 0;

  return (
    <>
      <Box bgcolor={"#fff"} className={classes.root} position={"relative"}>
        {(!solarReport || !lead || productsLoading) && (
          <Box
            position={"fixed"}
            top={0}
            left={0}
            width={"100%"}
            height="100vh"
            bgcolor={"rgba(255,255,255,0.5)"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            zIndex={1302}
          >
            <CircularProgress color="inherit" />
          </Box>
        )}
        <Container maxWidth={"lg"}>
          <Box display={"flex"} alignItems={"center"} justifyContent={"center"} width={"100%"} mt={6} mb={4}>
            <img className={classes.logo} src={process.env.PUBLIC_URL + "/images/logo.png"} alt="Logo" />
          </Box>
        </Container>
        {!solarReport || !lead || productsLoading ? (
          <Loader />
        ) : (
          <>
            {isMoreThanSm && (
              <Container maxWidth={"lg"}>
                <Benefit hideHeader={true} />
              </Container>
            )}
            <Box mb={6}>
              <Title name={lead.firstName} quoteType={EQuoteType.Solar} />
            </Box>

            {recommendedProduct && <OutcomePackage product={recommendedProduct} />}
            <GraphsSection solarReportData={solarReport} isSolar={true} />
            <EnvironmentalImpactSection solarReportData={solarReport} isGraphVisible={true} />
            <Box bgcolor={"#f7f7f7"} mt={"97px"} position={"relative"}>
              <TopBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />

              <FinancialSupports
                quoteType={EQuoteType.Solar}
                purchaserType={solarReport.purchaserType}
                hasSpecialBackground={true}
                loanAmount={price - grant}
              />

              <BotBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
            </Box>

            <WhyChooseUsSection hasSpecialBackground={false} />

            <Box mt={"105px"} mb={"124px"}>
              <CustomerSay
                customerSlider={
                  solarReport.purchaserType === EQuotePurchaserType.Business
                    ? customerSliderSolarBusiness
                    : customerSliderSolarHome
                }
              />
            </Box>

            {(relatedProducts?.length ?? 0) > 0 && (
              <Box bgcolor={"#f7f7f7"} my={"97px"} position={"relative"}>
                <TopBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />

                <OutcomePackageOptions products={relatedProducts} />

                <BotBox
                  hiddenButton={true}
                  bgColor="#f7f7f7"
                  outsideBgColor="#fff"
                  boxHeight={isMoreThanMd ? "145px" : "71px"}
                />
              </Box>
            )}

            {!isMoreThanSm && <BookNowButton />}
          </>
        )}
      </Box>
    </>
  );
};

const OutcomePackage = ({ product }: { product: Product }) => {
  const { isMoreThanMd, isMoreThanSm } = useBreakpoint();
  const classes = useQuoteInfoStyles();
  const matches = useMediaQuery("(max-width:1440px)");

  const price = product.prices[0].price;
  const grantAmountAvailable = solarGrantAmountForLeadProduct(product);

  const dispatch = useDispatch();

  const { financeIrelandEstimate } = useSelector((state: RootState) => state.quote);

  useEffect(() => {
    dispatch(fetchFinanceIrelandEstimate(price - grantAmountAvailable));
  }, [dispatch, price, grantAmountAvailable]);

  // const lineItem: QuoteLineItem = {
  //   productCode: product.code,

  //   name: product.name,
  //   description: product.description,
  //   highLevelDescription: product.highLevelDescription,
  //   highlights: product.highlights,
  //   // classification: product.classification,
  //   imageUrl: product.media[0].mediaFiles[0].url,
  //   // videoUrl?: string;

  //   price: product.prices[0].price,
  //   quantity: 1,
  //   // vatRate?: number;
  //   productId: product.id,
  //   type: EProductType.Product,
  //   selected: true,
  //   isFixedQuantity: true,
  //   maxQuantity: 1
  // };

  return (
    <>
      <Box
        bgcolor="#fff"
        display={"flex"}
        flexDirection={isMoreThanMd ? "row" : "column"}
        maxWidth={!matches ? "968px" : "968px"}
        my={4}
        mx={isMoreThanMd ? "auto" : 4}
      >
        <Box width={isMoreThanMd ? "auto" : "100%"}>
          <Box className={classes.box}>
            <Box className={classes.boxClipPath}>
              <Box className={classes.boxContent}>
                <RecommendedProductSlider product={product} />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box width={isMoreThanMd ? "auto" : "100%"} pl={isMoreThanMd ? "100px" : 0} mt={isMoreThanMd ? 0 : "30px"}>
          <Box maxWidth={isMoreThanSm ? "100%" : "272px"} mx={isMoreThanSm ? 0 : "auto"}>
            <QuoteInfoTitle quoteTitle={product.name} />
          </Box>

          <Box
            fontWeight={500}
            fontSize={isMoreThanSm ? "14px" : "12px"}
            display={"flex"}
            mt={isMoreThanSm ? "25px" : "18px"}
            alignItems={"start"}
            justifyContent={"space-between"}
            color={"#160037"}
          >
            <Box mr={2}>Final cost to you incl. grants</Box>
            <Box display={"flex"} flexDirection={"column"} alignItems={"end"}>
              <Box fontSize={isMoreThanSm ? "22px" : "18px"}>
                <span style={{ borderBottom: "2px solid #160037" }}>
                  <TF format="price" underline={true} fontSize="1.4rem" fontAccent={true}>
                    {price - Number(grantAmountAvailable || 0)}
                  </TF>
                </span>
              </Box>
              {price - Number(grantAmountAvailable || 0) < MAX_FINAL_COST &&
              price - Number(grantAmountAvailable || 0) > MIN_FINAL_COST ? (
                <>
                  <Box
                    fontSize={isMoreThanSm ? "11px" : "9px"}
                    fontWeight={500}
                    mt={isMoreThanSm ? "10px" : "5px"}
                    color="#84868c"
                  >
                    OR{" "}
                    <span style={{ fontSize: isMoreThanSm ? "15px" : "13px" }}>
                      <PriceFormat value={financeIrelandEstimate?.monthlyPayment || 0} />
                    </span>{" "}
                    pm
                  </Box>
                  <img src="images/financeIreland.png" alt="financeIreland" height={isMoreThanSm ? "11px" : "9px"} />
                </>
              ) : null}
            </Box>
          </Box>
          <Box my={3}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btnBookNow}
              href="https://swyftenergy4.schedulista.com/"
              target="_blank"
            >
              Book a Call
            </Button>
          </Box>
        </Box>
      </Box>
      <OutcomePackageInfo product={product} />
    </>
  );
};

const OutcomePackageInfo = ({ product }: { product: Product }) => {
  const { isMoreThanMd, isMoreThanSm } = useBreakpoint();
  const classes = useStyles();

  return (
    <Box bgcolor="#f7f7f7" mt={"97px"} position={"relative"}>
      <TopBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
      <Box
        textAlign={"center"}
        fontSize={isMoreThanSm ? "28px" : "24px"}
        color={"#1e073f"}
        fontWeight={500}
        width={"100%"}
        position={"absolute"}
        top={"67px"}
      >
        This package <span className={classes.colorOrange}>includes</span>
      </Box>
      <Container maxWidth={"lg"}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          pt={isMoreThanMd ? 0 : "60px"}
          justifyContent={"center"}
        >
          <Box
            bgcolor={"#fff"}
            width={"100%"}
            boxShadow={"0 5px 15px 0 rgba(0, 0, 0, 0.07)"}
            maxWidth={"740px"}
            borderRadius={"4px"}
          >
            <List>
              {product.highlights.map((x, i) => (
                <ListItem key={i} className={classes.bulletPoint}>
                  <ListItemText primary={x} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Container>
      <BotBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
    </Box>
  );
};

const OutcomePackageOptions = ({ products }: { products: Product[] }) => {
  const { isMoreThanSm } = useBreakpoint();
  const classes = useStyles();

  if (products === null || products.length === 0) {
    return <></>;
  }

  return (
    <>
      <Box
        textAlign={"center"}
        fontSize={isMoreThanSm ? "28px" : "24px"}
        color={"#1e073f"}
        fontWeight={500}
        width={"100%"}
        mb={4}
      >
        Not sure the one for you? Don't worry!
        <br />
        We have plenty of <span className={classes.colorOrange}>Solar PV packages</span> that suit your needs!
      </Box>
      <PackageInfoSlider products={products} />
    </>
  );
};
